import type {
  CostActivityEntry, CostAppointedBy, CostContract, CostContractFilter, CostContractKind, CostContractLineInput, CostContractStatus,
} from '../types'
import { percentFieldProblem } from './applications'
import type { ModuleActions } from './permissions'
import { CONTRACT_STATUSES, CONTRACT_STATUS_KEY } from './labels'
import { describeActivity, historyVariables, recordDetailState, type RecordDetailState } from './records'
import { normalizeSearch, type CostTree } from './tree'

export { CONTRACT_STATUSES, CONTRACT_STATUS_KEY, CONTRACT_KIND_KEY, APPOINTED_BY_KEY, CONTRACT_TYPE_KEY } from './labels'

type T = (key: string, opts?: Record<string, unknown>) => string

// ---- Money strings: the lines editor's running total only (ruling P7); every other figure is the server's ----

const CENTS = /^(-)?(\d+)(?:\.(\d{1,2}))?$/
const HUNDRED = BigInt(100)
const NO_CENTS = BigInt(0)

export const toCents = (value: string): bigint => {
  const m = CENTS.exec(value.trim())
  if (!m) throw new Error(`Not a decimal amount: ${value}`)
  const cents = BigInt(m[2]) * HUNDRED + BigInt((m[3] ?? '').padEnd(2, '0'))
  return m[1] ? -cents : cents
}
export const fromCents = (cents: bigint): string => {
  const negative = cents < NO_CENTS
  const abs = negative ? -cents : cents
  return `${negative ? '-' : ''}${(abs / HUNDRED).toString()}.${(abs % HUNDRED).toString().padStart(2, '0')}`
}

/** Exact sum of decimal strings ("1234.50"), in whole cents. */
export const sumMoneyStrings = (values: string[]): string => fromCents(values.reduce((acc, v) => acc + toCents(v), NO_CENTS))
export const subtractMoneyStrings = (a: string, b: string): string => fromCents(toCents(a) - toCents(b))
export const compareMoneyStrings = (a: string, b: string): -1 | 0 | 1 => {
  const d = toCents(a) - toCents(b)
  return d < NO_CENTS ? -1 : d > NO_CENTS ? 1 : 0
}

// ---- Register filter and grouping (spec §10) ----

export interface ContractFilterState {
  rootId: string | null
  kind: CostContractKind | null
  status: CostContractStatus | null
  appointedBy: CostAppointedBy | null
  search: string
}
export const EMPTY_CONTRACT_FILTER: ContractFilterState = { rootId: null, kind: null, status: null, appointedBy: null, search: '' }

/** The API filter: only the fields that are set; `search` is the debounced text. */
export function toContractFilter(s: ContractFilterState, search: string): CostContractFilter {
  const f: CostContractFilter = {}
  if (s.rootId) f.nodeId = s.rootId
  if (s.kind) f.kind = s.kind
  if (s.status) f.status = s.status
  if (s.appointedBy) f.appointedBy = s.appointedBy
  if (search.trim()) f.search = search.trim()
  return f
}
export const isFiltered = (s: ContractFilterState): boolean => !!(s.rootId || s.kind || s.status || s.appointedBy || s.search.trim())

export type ContractGrouping = 'levelOne' | 'status' | 'none'
export const MULTI_GROUP = 'multi'
export const NO_ALLOCATION_GROUP = 'unallocated'
export const MAIN_GROUP = 'main'
export interface ContractGroup { key: string; label: string; contracts: CostContract[] }

/** By level-1 group ("Více budov" when a contract spans several, "Bez rozpočtení" without lines), by status, or flat. */
export function groupContracts(rows: CostContract[], by: ContractGrouping, levelOne: { id: string; name: string }[], t: T): ContractGroup[] {
  if (by === 'none') return [{ key: 'all', label: '', contracts: rows }]
  if (by === 'status') {
    return CONTRACT_STATUSES
      .map((s) => ({ key: s, label: t(CONTRACT_STATUS_KEY[s]), contracts: rows.filter((c) => c.status === s) }))
      .filter((g) => g.contracts.length > 0)
  }
  // Main contracts carry no lines; they head the list instead of sitting under "Bez rozpočtení" (3a deferred item).
  const mains = rows.filter((c) => c.kind === 'MAIN')
  const subs = rows.filter((c) => c.kind !== 'MAIN')
  const known = new Set(levelOne.map((n) => n.id))
  const idsOf = (c: CostContract) => c.levelOneIds.filter((id) => known.has(id))
  const groups: ContractGroup[] = [{ key: MAIN_GROUP, label: t('CostControl_contracts_group_main'), contracts: mains }]
  levelOne.forEach((n) => groups.push({
    key: n.id, label: n.name, contracts: subs.filter((c) => { const ids = idsOf(c); return ids.length === 1 && ids[0] === n.id }),
  }))
  groups.push({ key: MULTI_GROUP, label: t('CostControl_contracts_group_multi'), contracts: subs.filter((c) => idsOf(c).length > 1) })
  groups.push({ key: NO_ALLOCATION_GROUP, label: t('CostControl_contracts_group_none'), contracts: subs.filter((c) => idsOf(c).length === 0) })
  return groups.filter((g) => g.contracts.length > 0)
}

// ---- Rights (spec §1.4, §7.1, §7.2) ----

export interface ContractAffordances {
  create: boolean
  fromTender: boolean
  editHeader: boolean
  editLines: boolean
  editPriceItems: boolean
  importPriceItems: boolean
  activate: boolean
  close: boolean
  reopen: boolean
  remove: boolean
  addAttachment: boolean
  removeAttachment: boolean
}

/** The server enforces the same rules; this only hides what it would refuse. `c` is null on the register. */
export function contractAffordances(a: ModuleActions, procurementView: boolean, c: Pick<CostContract, 'kind' | 'status' | 'tenderId'> | null, priceItemCount = 0): ContractAffordances {
  const open = !!c && c.status !== 'CLOSED'
  const editPriceItems = a.canEdit && c?.status === 'DRAFT' && c?.kind === 'SUB'
  return {
    create: a.canCreate,
    fromTender: a.canCreate && procurementView,
    editHeader: a.canEdit && open,
    // A price breakdown derives the allocation (2026-10-06 spec §1).
    editLines: a.canEdit && open && c?.kind === 'SUB' && priceItemCount === 0,
    editPriceItems,
    importPriceItems: editPriceItems && procurementView && !!c?.tenderId && priceItemCount === 0,
    activate: a.canEdit && c?.status === 'DRAFT',
    close: a.canEdit && c?.status === 'ACTIVE',
    reopen: a.canEdit && c?.status === 'CLOSED',
    remove: a.canDelete && c?.status === 'DRAFT',
    addAttachment: a.canCreate && !!c,
    removeAttachment: a.canDelete && !!c,
  }
}

/** Why the server would refuse "Aktivovat" (spec §1.4; price items: 2026-10-06 spec §1), as an i18n key, or null. */
export function activationBlocker(c: Pick<CostContract, 'kind' | 'status' | 'lineCount' | 'mainValue'>, unmappedCount = 0): string | null {
  if (c.status !== 'DRAFT') return null
  if (c.kind === 'SUB' && unmappedCount > 0) return 'CostControl_contract_activate_needs_mapping'
  if (c.kind === 'SUB' && c.lineCount === 0) return 'CostControl_contract_activate_needs_lines'
  if (c.kind === 'MAIN' && !(c.mainValue && compareMoneyStrings(c.mainValue, '0') > 0)) return 'CostControl_contract_activate_needs_value'
  return null
}

// ---- Detail page ----

export type ContractDetailState = RecordDetailState

/**
 * What the detail page shows. `message` is the latest refusal of its contract read: the page's own query, or a reload
 * after a write, which Apollo 2.6 does not hand to the page's query, and which can sit next to older cached data.
 * For someone who holds VIEW on the route's project a denial can only mean the contract is gone or belongs to
 * another project (the shield's resource loader is the only signal); until the rights have loaded it is not yet told
 * apart from "no permission". A contract of another project than the route's is not found either: the page would
 * otherwise show it with the route project's rights and items.
 */
export function contractDetailState(s: { loading: boolean; hasData: boolean; contractProjectId: string | null; projectId: string; message: string | null; rightsLoading: boolean; canView: boolean }): ContractDetailState {
  return recordDetailState({ ...s, recordProjectId: s.contractProjectId, notFoundKey: 'CostControl_error_contract_not_found' })
}

// ---- Header form ----

export interface ContractForm {
  kind: CostContractKind
  number: string
  name: string
  counterpartyName: string
  counterpartyIco: string
  appointedBy: CostAppointedBy
  mainValue: string | null
  mainValueValid: boolean
  signedAt: string | null
  startDate: string | null
  endDate: string | null
  notes: string
  retentionPercent: string | null
  retentionValid: boolean
}
export const emptyContractForm = (): ContractForm => ({
  kind: 'SUB', number: '', name: '', counterpartyName: '', counterpartyIco: '', appointedBy: 'GENERAL_CONTRACTOR',
  mainValue: null, mainValueValid: true, signedAt: null, startDate: null, endDate: null, notes: '', retentionPercent: null, retentionValid: true,
})
export const formFromContract = (c: CostContract): ContractForm => ({
  kind: c.kind, number: c.number ?? '', name: c.name, counterpartyName: c.counterpartyName, counterpartyIco: c.counterpartyIco ?? '',
  appointedBy: c.appointedBy, mainValue: c.mainValue, mainValueValid: true, signedAt: c.signedAt, startDate: c.startDate, endDate: c.endDate, notes: c.notes ?? '',
  retentionPercent: c.retentionPercent, retentionValid: true,
})
/** The first problem as an i18n key (the server checks the same), or null. `status` is the stored status when editing. */
export function contractFormProblem(f: ContractForm, status?: CostContractStatus): string | null {
  if (!f.name.trim()) return 'CostControl_error_name_required'
  if (!f.counterpartyName.trim()) return 'CostControl_error_counterparty_required'
  if (f.kind === 'MAIN' && !f.mainValueValid) return 'CostControl_error_bad_amount'
  if (f.kind === 'MAIN' && status === 'ACTIVE' && !(f.mainValue && compareMoneyStrings(f.mainValue, '0') > 0)) return 'CostControl_error_main_value_required'
  const retention = percentFieldProblem(f.retentionPercent, f.retentionValid)
  if (retention) return retention
  if (f.startDate && f.endDate && f.endDate < f.startDate) return 'CostControl_error_dates_order'
  return null
}
/** Form → API input: `kind` only on create, `mainValue` only for a main contract, empty text → null. */
export function contractInput(f: ContractForm, mode: 'create' | 'edit'): Record<string, unknown> {
  const text = (s: string) => s.trim() || null
  return {
    ...(mode === 'create' ? { kind: f.kind } : {}),
    number: text(f.number), name: f.name.trim(), counterpartyName: f.counterpartyName.trim(), counterpartyIco: text(f.counterpartyIco),
    appointedBy: f.appointedBy, signedAt: f.signedAt, startDate: f.startDate, endDate: f.endDate, notes: text(f.notes), retentionPercent: f.retentionPercent,
    ...(f.kind === 'MAIN' ? { mainValue: f.mainValue } : {}),
  }
}

// ---- Lines editor ----

export interface DraftLine { nodeId: string; amount: string | null; valid: boolean; description: string; provisional?: boolean }
export interface DraftCheck { total: string; problem: string | null; difference: string | null }

/**
 * Running total of the valid amounts and the first problem (an i18n key), mirroring the server's checkContractLines.
 * `requiredTotal` is set for an ACTIVE contract, whose re-allocation keeps the total; `difference` is total − required.
 */
export function checkDraftLines(lines: DraftLine[], requiredTotal: string | null): DraftCheck {
  const total = sumMoneyStrings(lines.filter((l) => l.valid && l.amount !== null).map((l) => l.amount as string))
  const difference = requiredTotal === null ? null : subtractMoneyStrings(total, requiredTotal)
  const nodeIds = lines.map((l) => l.nodeId)
  let problem: string | null = null
  if (lines.some((l) => !l.valid)) problem = 'CostControl_lines_problem_invalid'
  else if (lines.some((l) => l.amount === null || compareMoneyStrings(l.amount, '0') <= 0)) problem = 'CostControl_lines_problem_positive'
  else if (new Set(nodeIds).size !== nodeIds.length) problem = 'CostControl_lines_problem_duplicate'
  else if (difference !== null && compareMoneyStrings(difference, '0') !== 0) problem = 'CostControl_lines_problem_total'
  return { total, problem, difference }
}

/** Pro-rata lines into the draft: an item already present gets the amount added, a new one is appended. */
export function mergeAllocation(lines: DraftLine[], allocation: { nodeId: string; amount: string }[]): DraftLine[] {
  const next = lines.map((l) => ({ ...l }))
  allocation.forEach((a) => {
    const existing = next.find((l) => l.nodeId === a.nodeId)
    if (!existing) next.push({ nodeId: a.nodeId, amount: a.amount, valid: true, description: '' })
    else if (existing.valid && existing.amount !== null) existing.amount = sumMoneyStrings([existing.amount, a.amount])
    else { existing.amount = a.amount; existing.valid = true }
  })
  return next
}
type SavedLine = { nodeId: string; amount: string; description: string | null; provisional?: boolean }
export const draftFromLines = (lines: SavedLine[]): DraftLine[] =>
  lines.map((l) => ({ nodeId: l.nodeId, amount: l.amount, valid: true, description: l.description ?? '', ...(l.provisional ? { provisional: true } : {}) }))
export const toLineInputs = (lines: DraftLine[]): CostContractLineInput[] =>
  lines.map((l) => ({ nodeId: l.nodeId, amount: l.amount as string, description: l.description.trim() || null, provisional: !!l.provisional }))
/** True when the draft would save exactly what is stored (Save stays disabled). */
export const sameLines = (draft: DraftLine[], saved: SavedLine[]): boolean =>
  draft.length === saved.length && draft.every((d, i) => d.valid && d.nodeId === saved[i].nodeId && d.amount !== null
    && compareMoneyStrings(d.amount, saved[i].amount) === 0 && (d.description.trim() || null) === (saved[i].description ?? null)
    && !!d.provisional === !!saved[i].provisional)

// ---- Tree labels ----

/** "Budova A / Hrubá stavba": the groups above a node. */
export function nodePath(tree: CostTree, id: string): string {
  const names: string[] = []
  let p = tree.byId.get(id)?.parent ?? null
  while (p) {
    names.unshift(p.activity.name)
    p = p.parent
  }
  return names.join(' / ')
}

export interface ItemOption { id: string; name: string; code: string | null; path: string }
/**
 * Items in tree order for the item picker (groups too with `includeGroups`): not yet on a line, optionally only
 * `onlyIds`, matching the search on code, name or path (no diacritics).
 */
export function itemOptions(tree: CostTree, excludeIds: string[], query: string, onlyIds?: string[], includeGroups = false): ItemOption[] {
  const excluded = new Set(excludeIds)
  const only = onlyIds ? new Set(onlyIds) : null
  const q = normalizeSearch(query)
  return tree.order
    .filter((n) => (includeGroups || n.activity.kind === 'ITEM') && !excluded.has(n.activity.id) && (!only || only.has(n.activity.id)))
    .map((n) => ({ id: n.activity.id, name: n.activity.name, code: n.activity.code, path: nodePath(tree, n.activity.id) }))
    .filter((o) => !q || normalizeSearch(`${o.code ?? ''} ${o.name} ${o.path}`).includes(q))
}

/** `?allocate=<amount>` after "Z tendru": a canonical positive decimal string, or null. */
export function allocateParamValue(raw: unknown): string | null {
  if (typeof raw !== 'string' || !/^\d{1,13}(\.\d{1,2})?$/.test(raw)) return null
  return compareMoneyStrings(raw, '0') > 0 ? raw : null
}

// ---- History ----

export const contractHistoryVariables = (projectId: string, contractId: string) => historyVariables(projectId, 'CONTRACT', contractId)

export const describeContractActivity = (e: Pick<CostActivityEntry, 'action' | 'meta'>, t: T, money: (v: string) => string) =>
  describeActivity({ ...e, entityType: 'CONTRACT' }, t, money)
